import BlockedIcon from "@mui/icons-material/Block";
import GroupIcon from "@mui/icons-material/Groups";
import DashboardIcon from "@mui/icons-material/Speed";
import QuestionIcon from "@mui/icons-material/Help";
import PencilIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SourceIcon from "@mui/icons-material/Source";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import StyleIcon from "@mui/icons-material/Style";
import { useMatch, Link } from "react-router-dom";
import { MenuItem, Tooltip, Typography, useTheme } from "@mui/material";
import type { FunctionComponent } from "react";

import type { RootState } from "../store/state/rootReducer";

import { makeStyles, useScreenSize } from "./theme/theme";

export interface IIconWrapperProps {
  fontSize?: number;
  otherStyleProps?: any;
  icon: (props: any) => JSX.Element;
  tooltipTitle?: string;
}

function IconWrapper({ fontSize = 24, otherStyleProps = {}, icon: Component, tooltipTitle }: IIconWrapperProps) {
  const { isSmall } = useScreenSize();
  const { mobileScalingFactor, iconScalingFactor } = useTheme();

  const customfontsize = fontSize * (isSmall ? mobileScalingFactor * (1 + iconScalingFactor) : 1);

  const IconComponent = <Component style={{ fontSize: customfontsize, ...otherStyleProps }} />;
  return tooltipTitle ? (
    <Tooltip title={tooltipTitle}>
      <span>{IconComponent}</span>
    </Tooltip>
  ) : (
    IconComponent
  );
}

export const iconMap: Record<string, FunctionComponent> = {
  home: () => <IconWrapper icon={HomeIcon} />,
  dashboard: () => <IconWrapper icon={DashboardIcon} fontSize={26} />,
  blocked: () => <IconWrapper icon={BlockedIcon} />,
  group: () => <IconWrapper icon={GroupIcon} fontSize={28} />,
  question: () => <IconWrapper fontSize={20} icon={QuestionIcon} />,
  pencil: () => <IconWrapper fontSize={20} icon={PencilIcon} />,
  projects: () => <IconWrapper fontSize={20} icon={SourceIcon} tooltipTitle="Projects" />,
  organizations: () => <IconWrapper fontSize={20} icon={AccountBalanceIcon} tooltipTitle="Organizations" />,
  myInterviews: () => <IconWrapper fontSize={20} icon={RecordVoiceOverIcon} tooltipTitle="Recordings" />,
  clips: () => <IconWrapper fontSize={20} icon={VideoLibraryIcon} tooltipTitle="Clips" />,
  galleries: () => <IconWrapper fontSize={20} icon={OndemandVideoIcon} tooltipTitle="Galleries" />,
  portals: () => <IconWrapper fontSize={20} icon={WebAssetIcon} tooltipTitle="Portals" />,
  remixes: () => <IconWrapper fontSize={20} icon={SlowMotionVideoIcon} tooltipTitle="Remixes" />,
  themes: () => <IconWrapper fontSize={20} icon={StyleIcon} tooltipTitle="Themes" />,
};

const useClasses = makeStyles()((theme) => ({
  headingStyle: {
    fontWeight: "bold",
  },
  leafStyle: {
    fontWeight: "lighter",
  },
  links: {
    color: `${theme.palette.getContrastText(theme.palette.primary.main)} !important`,
    textDecoration: "none !important",
  },
  subMenu: {
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1),
    },
  },
  menuItem: {
    paddingTop: theme.spacing(1.4),
    paddingBottom: theme.spacing(1.4),
    "&.Mui-selected": {
      backgroundColor: "rgba(0,0,0,0.15) !important",
    },
  },
}));

export interface IDrawerMenuItemRowProps {
  route: RootState["frame"]["nav"]["itemsById"][""];
  setIsExpanded: (expanded: boolean) => void;
}

export function DrawerMenuItemRow({ route, setIsExpanded }: IDrawerMenuItemRowProps) {
  const exactMatch = useMatch(route.href);
  const { isSmall } = useScreenSize();

  const { classes } = useClasses();

  return (
    <Link
      key={route.href}
      className={classes.links}
      to={route.href}
      onClick={() => {
        if (isSmall) {
          setIsExpanded(false);
        }
      }}
    >
      <MenuItem dense={isSmall} className={classes.menuItem} selected={!!exactMatch}>
        <>
          {route.disable_icon ? null : route.icon && iconMap[route.icon]}
          <Typography className={route.children.length > 0 ? classes.headingStyle : classes.leafStyle}>
            {route.title}
          </Typography>
        </>
      </MenuItem>
    </Link>
  );
}
